import {
  AI_REVIEW_THRESHOLD,
  EXCLUSION_LABELS,
  type AccountCodeOrigin,
  type EstimateCell,
  type ExpenseAiRun,
  type ExpenseLineDetail,
  type Expense,
  type ExpenseExclusionReason,
  type YearEndProjection,
} from '@cockpit/shared';
import { eur, eurCents, monthLong, monthTitle } from './format';
import type { SearchOption } from './searchSelect';

/**
 * Lot P (onglet Dépenses) : logique d'affichage pure, testée. Badges d'origine du poste, motifs « Pas une
 * dépense », phrase de fin d'année, saisie d'une estimation en euros.
 */

export interface Badge {
  label: string;
  title: string;
  /** Classe de couleur de `.chip` (`c-acc`, `c-warn`, `c-ok`…). */
  tone: string;
}

/** Badge de l'origine du poste d'une ligne : IA (avec sa confiance), règle, vous, compte, Sheet. */
export function sourceBadge(e: Pick<Expense, 'categorySource' | 'categoryConfidence'>): Badge | null {
  switch (e.categorySource) {
    case 'ai': {
      const confidence = e.categoryConfidence ?? 0;
      const pct = Math.round(confidence * 100);
      return {
        label: `IA ${pct} %`,
        title:
          confidence < AI_REVIEW_THRESHOLD
            ? `Poste proposé par l'IA, peu sûr (${pct} %) : à vérifier`
            : `Poste proposé par l'IA (confiance ${pct} %)`,
        tone: confidence < AI_REVIEW_THRESHOLD ? 'c-warn' : 'c-acc',
      };
    }
    case 'rule':
      return { label: 'règle', title: 'Rangée par une règle apprise (opérations similaires)', tone: 'c-ok' };
    case 'user':
      return { label: 'vous', title: 'Poste choisi par un membre de l’équipe', tone: 'c-ok' };
    case 'account_code':
      return { label: 'compte', title: 'Rangée par son compte comptable Axonaut', tone: '' };
    case 'import':
      return { label: 'Sheet', title: 'Montant importé du Google Sheet', tone: '' };
    default:
      return null;
  }
}

export const EXCLUSION_OPTIONS = (Object.keys(EXCLUSION_LABELS) as ExpenseExclusionReason[]).map((value) => ({
  value,
  label: EXCLUSION_LABELS[value],
}));

/** Pourquoi une ligne n'est pas comptée. */
export function notCountedText(
  e: Pick<Expense, 'excludedReason' | 'excludedBy' | 'duplicateOf'>,
): string | null {
  if (e.excludedReason)
    return `${EXCLUSION_LABELS[e.excludedReason]}${e.excludedBy === 'rule' ? ' (règle par défaut)' : ''}`;
  if (e.duplicateOf) return `Doublon de « ${e.duplicateOf.label} » (${eur(e.duplicateOf.amountCents)} HT)`;
  return null;
}

/** Infobulle d'un montant : TTC compté HT, ou TTC d'une ligne sans poste. */
export function amountTitle(
  e: Pick<Expense, 'isTaxIncluded' | 'vatConverted' | 'countedCents'>,
): string | undefined {
  if (!e.isTaxIncluded) return undefined;
  if (e.vatConverted)
    return `Ligne bancaire TTC : comptée ${eurCents(e.countedCents)} HT (÷ 1,2, poste à TVA récupérable)`;
  return 'Ligne bancaire : montant TTC, compté tel quel (poste sans TVA récupérable ou sans poste)';
}

/** « Fin d'année estimée : X € de dépenses (réel Y + estimé Z) ». */
export function yearEndText(y: YearEndProjection): string {
  return `Fin d'année estimée : ${eur(y.totalCents)} de dépenses (réel ${eur(y.actualCents)} + estimé ${eur(y.estimatedCents)})`;
}

/** Saisie d'une estimation en euros (« 1 250 », « 1250,5 ») → centimes ; vide → null ; invalide → undefined. */
export function parseEurosInput(text: string): number | null | undefined {
  const cleaned = text.replace(/[\s\u00a0\u202f€]/g, '').replace(',', '.');
  if (cleaned === '') return null;
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return undefined;
  return Math.round(Number(cleaned) * 100);
}

const ORIGIN_LABELS: Record<EstimateCell['origin'], string> = {
  manual: 'saisi à la main',
  ai: 'proposé par Claude',
  average: 'moyenne des 3 derniers mois complets',
  seasonal: "même mois de l'an dernier (poste saisonnier)",
  import: 'prévision du Sheet',
};

export function originLabel(origin: EstimateCell['origin']): string {
  return ORIGIN_LABELS[origin];
}

// ------------------------------------------------------------------ Lot Y (retours d'Inès du 13/10/2026)

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n > 1 ? many : one}`;

/** Un passage de l'IA en cours (en file ou en train de tourner) : le bouton ne relance rien. */
export function isAiRunActive(run: Pick<ExpenseAiRun, 'status'> | null | undefined): boolean {
  return run?.status === 'queued' || run?.status === 'running';
}

/** Avancement d'un passage, de 0 à 1. */
export function aiRunRatio(run: Pick<ExpenseAiRun, 'total' | 'attempted' | 'status'>): number {
  if (run.total <= 0) return run.status === 'queued' || run.status === 'running' ? 0 : 1;
  return Math.min(1, run.attempted / run.total);
}

/**
 * Phrase sous la barre de progression [14] : « Paquet 2 sur 3 · 50 sur 120 lignes » pendant le passage, puis le
 * bilan (« 120 lignes traitées : 100 rangées, dont 9 à vérifier ; 20 restent à faire à la main. »), ou la note
 * du serveur pour un arrêt (plafond, erreur, Claude non configuré).
 */
export function aiRunText(run: ExpenseAiRun): string {
  if (run.status === 'queued') return `En attente de démarrage · ${plural(run.total, 'ligne')} à traiter`;
  if (run.status === 'running') {
    const current = Math.min(run.batchCount, run.batchesDone + 1);
    return `Paquet ${current} sur ${Math.max(1, run.batchCount)} · ${run.attempted} sur ${plural(run.total, 'ligne')}`;
  }
  if (run.status === 'done') {
    const left = run.attempted - run.categorized;
    return `${plural(run.attempted, 'ligne traitée', 'lignes traitées')} : ${plural(run.categorized, 'rangée', 'rangées')}${
      run.review > 0 ? `, dont ${run.review} à vérifier` : ''
    }${left > 0 ? ` ; ${plural(left, 'reste', 'restent')} à faire à la main` : ''}.${run.demo ? ' (Démonstration : mots-clés, sans Claude.)' : ''}`;
  }
  return run.note;
}

/** Libellé d'un seuil de « À vérifier » (0 = toutes). */
export function reviewChoiceLabel(cents: number): string {
  return cents <= 0 ? 'toutes' : eur(cents);
}

/** Origine d'une correspondance compte → poste [15] : « par défaut », « ajouté par Inès le 14 oct. ». */
export function codeOriginText(origin: AccountCodeOrigin): string {
  if (origin.kind === 'default') return 'par défaut';
  if (origin.kind === 'unknown') return 'ajouté avant le suivi';
  const when = origin.at
    ? new Date(origin.at).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        timeZone: 'Europe/Paris',
      })
    : null;
  return `ajouté${origin.byName ? ` par ${origin.byName.split(' ')[0]}` : ''}${when ? ` le ${when}` : ''}`;
}

/** Justificatif d'une ligne dans Axonaut [17]. */
export function attachmentText(value: ExpenseLineDetail['attachment']): string {
  if (value === 'present') return 'présent dans Axonaut';
  if (value === 'absent') return 'aucun dans Axonaut';
  return 'non indiqué par Axonaut';
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const joinFr = (items: readonly string[]) =>
  items.length <= 1 ? (items[0] ?? '') : `${items.slice(0, -1).join(', ')} et ${items[items.length - 1]}`;

/**
 * Aide du tableau [11] : « Novembre et décembre sont estimés : clique dans une case pour corriger le montant. Une
 * case corrigée passe en orange ; ↺ remet le calcul automatique. Octobre mélange le réel déjà reçu et
 * l'estimation. » `months` : mois en prévision de l'année, mois en cours en premier.
 */
export function forecastHelp(months: readonly string[]): string {
  const [current, ...future] = months;
  if (!current) return '';
  const names = future.map(monthLong);
  const lead =
    names.length === 0
      ? `Clique dans une case de ${monthLong(current)} pour corriger le montant du mois.`
      : `${capitalize(joinFr(names))} ${names.length > 1 ? 'sont estimés' : 'est estimé'} : clique dans une case pour corriger le montant.`;
  return `${lead} Une case corrigée passe en orange ; ↺ remet le calcul automatique. ${monthTitle(current)} mélange le réel déjà reçu et l'estimation.`;
}

// ------------------------------------------------------------------ Lot AH (retours du 14/10/2026)

/** Préfixe des options « Pas une dépense » dans les listes de catégorisation [20]. */
export const EXCLUDE_PREFIX = 'exclude:';

/**
 * Lot AH [20] : les choix « Pas une dépense » (mêmes motifs que dans le tableau), en tête des listes de
 * « À catégoriser », marqués « Pas une dépense » (texte secondaire cherchable).
 */
export const EXCLUSION_SEARCH_OPTIONS: readonly SearchOption[] = EXCLUSION_OPTIONS.map((o) => ({
  value: `${EXCLUDE_PREFIX}${o.value}`,
  label: o.label,
  secondary: 'Pas une dépense',
}));

export type CategoryChoice = { categoryId: string } | { excludedReason: ExpenseExclusionReason };

/** Valeur choisie dans une liste de catégorisation : un poste, ou un motif « Pas une dépense ». */
export function parseCategoryChoice(value: string): CategoryChoice {
  if (value.startsWith(EXCLUDE_PREFIX)) {
    const reason = value.slice(EXCLUDE_PREFIX.length) as ExpenseExclusionReason;
    if (reason in EXCLUSION_LABELS) return { excludedReason: reason };
  }
  return { categoryId: value };
}

export interface CategoryLike {
  id: string;
  name: string;
  kind: 'fixed' | 'variable';
  archived?: boolean;
}

/**
 * Options des postes (fixes puis variables, dans l'ordre reçu). Un poste archivé (lot AH [21]) n'est jamais
 * proposé ; avec `keepArchived`, il reste affiché mais désactivé, pour montrer le poste actuel d'une ligne.
 */
export function categorySearchOptions(
  categories: readonly CategoryLike[],
  options: { keepArchived?: boolean } = {},
): SearchOption[] {
  const active = categories.filter((c) => !c.archived);
  const out: SearchOption[] = [
    ...active.filter((c) => c.kind === 'fixed'),
    ...active.filter((c) => c.kind === 'variable'),
  ].map((c) => ({
    value: c.id,
    label: c.name,
    secondary: c.kind === 'fixed' ? 'Charge fixe' : 'Charge variable',
  }));
  if (options.keepArchived) {
    for (const c of categories.filter((x) => x.archived))
      out.push({ value: c.id, label: c.name, secondary: 'Poste archivé', disabled: true });
  }
  return out;
}

/**
 * Montant d'une case en prévision [19] : euros entiers groupés à la française (« 12 450 »), relus par
 * `parseEurosInput` (les espaces sont ignorés).
 */
export function forecastInputText(cents: number): string {
  return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(Math.round(cents / 100));
}

/**
 * Largeur du texte d'une case en prévision [19] (le champ ajoute ses marges) : le chiffre n'est jamais rogné,
 * au moins 5 caractères, un de plus pour le confort.
 */
export function forecastInputWidth(text: string): string {
  return `${Math.max(5, text.length) + 1}ch`;
}

/** Lot BM [16] : part d'une ligne ou d'une section dans le total des dépenses de la période. */
export interface ExpenseShare {
  /** Part (0,124 = 12,4 %), null quand le total n'est pas positif. */
  ratio: number | null;
  /** « 12,4 % », « — ». */
  text: string;
  /** Largeur de la mini-barre (0 à 100), relative au plus gros poste. */
  width: number;
}

/** « 12,4 % » (une décimale au plus). */
export function shareText(ratio: number): string {
  return `${(Math.round(ratio * 1000) / 10).toLocaleString('fr-FR', { maximumFractionDigits: 1 })}\u202f%`;
}

/**
 * Lot BM [16] : colonne « Part » du tableau des postes. Le total de la période = la somme des totaux des lignes
 * affichées, « À catégoriser » compris (les virements internes et autres « Pas une dépense » n'y sont jamais) : les
 * parts font 100 %. Une section prend la part de toutes ses lignes ; la mini-barre se mesure au plus gros poste.
 */
export function expenseShares(
  sections: readonly { title: string; rows: readonly { key: string; totalCents: number }[] }[],
): { total: number; rows: Map<string, ExpenseShare>; sections: Map<string, ExpenseShare> } {
  const all = sections.flatMap((s) => s.rows);
  const total = all.reduce((sum, r) => sum + r.totalCents, 0);
  const biggest = Math.max(0, ...all.map((r) => r.totalCents));
  const share = (cents: number): ExpenseShare =>
    total > 0
      ? {
          ratio: cents / total,
          text: shareText(cents / total),
          width: biggest > 0 ? Math.max(0, Math.min(100, Math.round((cents / biggest) * 100))) : 0,
        }
      : { ratio: null, text: '—', width: 0 };
  return {
    total,
    rows: new Map(all.map((r) => [r.key, share(r.totalCents)])),
    sections: new Map(
      sections.map((s) => [s.title, share(s.rows.reduce((sum, r) => sum + r.totalCents, 0))]),
    ),
  };
}
